import type { RuntimeConfig } from './auth';

/** A non-2xx response from the Watchtower API. `status` is the HTTP status;
 *  `message` is the server's `error` field when the body was JSON, else the raw
 *  body text. */
export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

/** The Watchtower Console is a READ-ONLY app — GET is the only verb. */
export interface Api {
  get<T>(path: string): Promise<T>;
}

export function makeApi(
  config: RuntimeConfig,
  getToken: () => string | null | Promise<string | null>,
): Api {
  const call = async <T>(path: string, init: RequestInit): Promise<T> => {
    // Await the getter: a sync (non-promise) return resolves immediately, so
    // sync call sites are unaffected; an async getter (ensureFreshToken)
    // refreshes-if-needed before the header is built.
    const token = await getToken();
    const res = await fetch(`${config.apiUrl}${path}`, {
      ...init,
      headers: {
        ...(init.headers ?? {}),
        authorization: token ? `Bearer ${token}` : '',
      },
    });
    const text = await res.text();
    if (!res.ok) {
      let msg = text;
      try { msg = (JSON.parse(text) as { error?: string }).error ?? text; } catch { /* raw */ }
      throw new ApiError(res.status, msg);
    }
    return JSON.parse(text) as T;
  };
  return {
    get: (path) => call(path, { method: 'GET' }),
  };
}
